/* eslint-disable no-nested-ternary */
import { StytchProvider } from '@stytch/react';
import React, {
  CSSProperties,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import {
  AutoConnect,
  ConnectButton as ThirdwebConnectButton,
  useActiveWallet,
  useActiveWalletChain,
  useConnectModal,
} from 'thirdweb/react';

import { useLocation } from '@reach/router';
import { StytchHeadlessClient } from '@stytch/vanilla-js/dist/index.headless';
import { useConnector, useEthers } from '@usedapp/core';

import Modal from '@bitcoin-portal/verse-web-components/dist/Modal';

import {
  BITCOIN_COM_LOGO_PATH,
  RABBY_AVATAR_URL,
  STYTCH_KEY,
} from '@context/constants';
import { useEns } from '@context/providers/EnsProvider';
import { useDispatch, useTrackedState } from '@context/store';
import {
  hasThirdwebClient,
  thirdwebAppMetadata,
  thirdwebAutoConnect,
  chains as thirdwebChains,
  client as thirdwebClient,
  thirdwebConnectDetailsModal,
  thirdwebConnectModal,
  thirdwebTheme,
  wallets as thirdwebWallets,
} from '@context/thirdweb';

import {
  getBlockchain,
  getDeviceSize,
  getEntrypoint,
  logAmplitudeEvent,
  shortAddress,
} from '@helpers/index';

import { useAppWallet } from '@hooks/useAppWallet';
import { useIsLoading } from '@hooks/useIsLoading';
import { useWindowSize } from '@hooks/useWindowSize';

import CheckEmailModal from '../CheckEmailModal';
import Image from '../Image';
import LoginModal from './LoginModal';
import ThirdwebSync from './ThirdwebSync';
import {
  ImageWrapper,
  ModalWrapper,
  StyledButton,
  ThirdwebButtonWrapper,
} from './styled';

interface Props {
  isDashboard?: boolean;
}

const thirdwebConnectButtonStyle: CSSProperties = {
  height: '36px',
  padding: '0 16px',
  borderRadius: '999px',
  fontSize: '14px',
  fontWeight: 600,
  background: 'linear-gradient(90deg, #2563eb 0%, #9333ea 100%)',
  color: '#ffffff',
};

const LoginButton: FunctionComponent<Props> = ({ isDashboard = true }) => {
  const loginModalRef = useRef<Modal>(null);
  const checkEmailModalRef = useRef<Modal>(null);
  const isPageLoading = useIsLoading();
  const username = useEns();
  const { pathname } = useLocation();
  const { provider, userDetails, connectModalOpen, isWallet } =
    useTrackedState();
  const dispatch = useDispatch();
  const { activateBrowserWallet, chainId } = useEthers();

  const { account: appAccount, isConnected: appConnected } = useAppWallet();

  const { connector } = useConnector();
  const thirdwebWallet = useActiveWallet();
  const thirdwebChain = useActiveWalletChain();
  const { connect: openThirdwebConnectModal } = useConnectModal();
  // Falls back to thirdweb's own account address so the connected pill
  // (icon/short address) shows immediately, without waiting for
  // ThirdwebSync to finish bridging the session into @usedapp/core.
  const account = appAccount;
  const [imgSrc, setImgScr] = useState('');

  const { width } = useWindowSize();

  const [connected, setConnected] = useState(false);
  const [isMetaMask, setIsMetaMask] = useState(false);
  const [isRabby, setIsRabby] = useState(false);
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [stytch, setStytch] = useState<StytchHeadlessClient>();
  const [mounted, setMounted] = useState(false);

  // Bitcoin.com CeFi (Stytch email) login is a separate product feature from
  // wallet connect, only surfaced for the sideshift/changenow providers off
  // the pools/farms/staking pages — untouched by the thirdweb migration below.
  const cefiApplicable = useMemo(() => {
    if (!['sideshift', 'changenow'].includes(provider)) return false;
    return !(
      pathname.includes('/pools') ||
      pathname.includes('/farms') ||
      pathname.includes('/staking')
    );
  }, [provider, pathname]);

  // The Bitcoin.com in-app wallet (embedded webview) and the CeFi flow both
  // keep using the legacy custom modal; everywhere else gets thirdweb's
  // <ConnectButton>. Also falls back here if no thirdweb client id is
  // configured (GATSBY_THIRDWEB_CLIENT_ID unset) rather than rendering a
  // broken button.
  const useLegacyConnectFlow = isWallet || cefiApplicable || !hasThirdwebClient;

  const trackLoginClick = useCallback(() => {
    if (!connected && typeof window !== 'undefined' && width) {
      logAmplitudeEvent('connectWalletClicked', {
        blockchain: getBlockchain(provider),
        'current page': window.location,
        entrypoint: getEntrypoint(),
        'screen size': getDeviceSize(width),
      });
    }
  }, [connected, provider, width]);

  const zIndex = 9999;

  useEffect(() => {
    setStytch(new StytchHeadlessClient(STYTCH_KEY));
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Every in-page "Connect Wallet" prompt across the app (Swap, Pools,
  // Farms, Staking, Vote, Burn, ...) dispatches SET_CONNECT_MODAL_OPEN
  // expecting a wallet-connect UI to appear here. The legacy <Modal
  // ref={loginModalRef}> below only renders when useLegacyConnectFlow is
  // true, so opening it via the ref is a no-op under the thirdweb flow —
  // open thirdweb's own connect modal imperatively instead in that case.
  useEffect(() => {
    if (!connectModalOpen) {
      loginModalRef?.current?.close();
      return;
    }

    if (useLegacyConnectFlow) {
      loginModalRef?.current?.open();
      return;
    }

    openThirdwebConnectModal({
      client: thirdwebClient as NonNullable<typeof thirdwebClient>,
      wallets: thirdwebWallets,
      chains: thirdwebChains,
      theme: thirdwebTheme,
      ...thirdwebConnectModal,
    })
      .catch(() => {
        // user closed the modal without connecting
      })
      .finally(() => {
        dispatch({ type: 'SET_CONNECT_MODAL_OPEN', payload: false });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectModalOpen]);

  useEffect(() => {
    setConnected(!!(userDetails || appConnected));
  }, [userDetails, appConnected]);

  useEffect(() => {
    const name = connector?.connector?.name?.toLowerCase() ?? '';
    const walletId = thirdwebWallet?.id ?? '';
    setIsMetaMask(name === 'metamask' || walletId === 'io.metamask');
    setIsRabby(name === 'rabby' || walletId === 'io.rabby');
  }, [connector, thirdwebWallet]);

  useEffect(() => {
    const activeChainId = chainId ?? thirdwebChain?.id;
    if (activeChainId === 1) {
      setImgScr('/images/uploads/eth.png');
    } else if (activeChainId === 137) {
      setImgScr('/images/uploads/ply.png');
    } else if (activeChainId === 10000) {
      setImgScr('/images/uploads/smartbch.png');
    } else {
      setImgScr('');
    }
  }, [chainId, thirdwebChain]);

  const buttonDesign = connected ? 'secondary' : 'primary';
  const connectedAccount = userDetails
    ? userDetails?.email
    : shortAddress(account);

  const displayName = username || connectedAccount;

  // Shared connected-state contents (address/email + wallet icon + chain
  // badge) so the thirdweb ConnectButton's details button looks identical
  // to the legacy connected pill below.
  const connectedButtonContent = (
    <>
      <span>{connected ? <>{displayName}</> : 'Connect'}</span>
      {connected && (
        <span>
          {!!userDetails && (
            <Image
              src={BITCOIN_COM_LOGO_PATH}
              alt="bitcoin.com icon"
              width={24}
            />
          )}
          {!!account && (
            <Image
              src={
                isMetaMask
                  ? '/images/uploads/metamask.png'
                  : isRabby
                  ? RABBY_AVATAR_URL
                  : BITCOIN_COM_LOGO_PATH
              }
              alt={
                isMetaMask
                  ? 'MetaMask icon'
                  : isRabby
                  ? 'Rabby Wallet icon'
                  : 'Bitcoin.com icon'
              }
              width={24}
              style={
                isRabby
                  ? { background: 'white', borderRadius: '50px' }
                  : undefined
              }
            />
          )}
          {imgSrc && (
            <ImageWrapper>
              <Image src={imgSrc} alt={`chain: ${chainId}`} height={8} />
            </ImageWrapper>
          )}
        </span>
      )}
    </>
  );

  return (
    <>
      {mounted && hasThirdwebClient && (
        <AutoConnect
          client={thirdwebClient as NonNullable<typeof thirdwebClient>}
          wallets={thirdwebWallets}
          timeout={thirdwebAutoConnect.timeout}
        />
      )}

      {mounted && <ThirdwebSync />}

      {useLegacyConnectFlow ? (
        <StyledButton
          chainImg={!!imgSrc}
          small
          disabled={isPageLoading}
          onClick={(e: React.UIEvent<HTMLElement>) => {
            if (isWallet && !connected) {
              activateBrowserWallet({
                type: 'bcomwallet',
              });

              dispatch({ type: 'SET_LOGIN_FROM' });
              trackLoginClick();
              return;
            }

            e.stopPropagation();
            loginModalRef?.current?.open();
            dispatch({ type: 'SET_LOGIN_FROM' });
            trackLoginClick();
          }}
          design={buttonDesign}
          wider={!!username}
          connected={connected}
        >
          {connectedButtonContent}
        </StyledButton>
      ) : (
        mounted && (
          <ThirdwebButtonWrapper>
            {/* client is defined here: this branch only renders when useLegacyConnectFlow is false, which requires hasThirdwebClient */}
            <ThirdwebConnectButton
              client={thirdwebClient as NonNullable<typeof thirdwebClient>}
              wallets={thirdwebWallets}
              chains={thirdwebChains}
              appMetadata={thirdwebAppMetadata}
              detailsModal={thirdwebConnectDetailsModal}
              theme={thirdwebTheme}
              connectModal={thirdwebConnectModal}
              connectButton={{
                label: 'Connect',
                style: thirdwebConnectButtonStyle,
              }}
              detailsButton={{
                connectedAccountAvatarUrl: BITCOIN_COM_LOGO_PATH,
                // Reuse the same pill used by the legacy flow so a
                // thirdweb-connected wallet looks identical to a
                // legacy-connected one. Clicking still opens thirdweb's
                // own account details modal (thirdweb wraps this in its
                // own onClick handler).
                render: () => (
                  <StyledButton
                    chainImg={!!imgSrc}
                    small
                    design="secondary"
                    wider={!!username}
                    connected
                  >
                    {connectedButtonContent}
                  </StyledButton>
                ),
              }}
            />
          </ThirdwebButtonWrapper>
        )
      )}

      {useLegacyConnectFlow && stytch && (
        <ModalWrapper $isDashboard={isDashboard}>
          <StytchProvider stytch={stytch}>
            <Modal
              ref={loginModalRef}
              title={<FormattedMessage id="global.labels.connectWallet" />}
              maxWidth="420px"
              callback={({ action }) => {
                if (action === 'close') {
                  setEmail('');
                  dispatch({
                    type: 'SET_CONNECT_MODAL_OPEN',
                    payload: false,
                  });
                } else {
                  dispatch({
                    type: 'SET_CONNECT_MODAL_OPEN',
                    payload: true,
                  });
                }
              }}
              zIndex={zIndex}
              noPadding
            >
              <LoginModal
                loginModalRef={loginModalRef}
                checkEmailModalRef={checkEmailModalRef}
                updateEmail={(input: string) => setEmail(input)}
                email={email}
                setSubmittedEmail={setSubmittedEmail}
              />
            </Modal>
            <Modal
              ref={checkEmailModalRef}
              title={<FormattedMessage id="global.labels.checkEmail" />}
              maxWidth="420px"
              zIndex={zIndex}
            >
              <CheckEmailModal email={submittedEmail} />
            </Modal>
          </StytchProvider>
        </ModalWrapper>
      )}
    </>
  );
};

export default LoginButton;
